import type { DateRange } from "@cubejs-client/core";
import { dateRangeDays } from "@hightouch/lib/ad-studio/insights-malloy/schemas/date-range";
import { err, isErr, ok, type Result } from "@hightouch/lib/util/result";
import { format } from "date-fns";
import { z } from "zod/v4";
import type { DateRangePreset } from "src/pages/ad-studio/insights/insights-date-range-picker";
import type { DayRange } from "src/pages/ad-studio/insights/performance/types";
import { TIMEZONE } from "src/pages/ad-studio/insights/performance/constants";
const isoDate = z.iso.date();

export const dayRangeSchema = z
  .object({ from: isoDate, to: isoDate })
  .refine((range) => range.from <= range.to, {
    message: "The range must not end before it starts",
  })
  .brand<"DayRange">();

export function toDateStr(d: Date): string {
  return format(d, "yyyy-MM-dd");
}

export function formatDateRangeLabel(from: string, to: string): string {
  const start = new Date(from + "T00:00:00");
  const end = new Date(to + "T00:00:00");
  return `${format(start, "MMM d")} – ${format(end, "MMM d, yyyy")}`;
}

const daysNow = (dateRange: DateRange): Result<DayRange, "not-a-day-range"> => {
  const resolved = dateRangeDays({
    dateRange,
    timezone: TIMEZONE,
    now: new Date(),
  });
  if (isErr(resolved)) {
    return err("not-a-day-range");
  }
  const [from, to] = resolved.value;
  const days = dayRangeSchema.safeParse({ from, to });
  return days.success ? ok(days.data) : err("not-a-day-range");
};

const SUB_DAY_UNIT = /\b(hours?|minutes?|seconds?)\b/i;

export const insightsDateRangeSchema = z
  .union([
    z.string().refine((dateRange) => !SUB_DAY_UNIT.test(dateRange), {
      message: "A date range counts whole days",
    }),
    z.tuple([z.iso.date(), z.iso.date()]),
  ])
  .superRefine((dateRange, context) => {
    if (isErr(daysNow(dateRange))) {
      context.addIssue({
        code: "custom",
        message: `${JSON.stringify(dateRange)} is not a day range`,
      });
    }
  })
  .brand<"InsightsDateRange">();
export type InsightsDateRange = z.output<typeof insightsDateRangeSchema>;

const DATE_PRESETS = (
  [
    { label: "Today", dateRange: "Today" },
    { label: "Yesterday", dateRange: "Yesterday" },
    { label: "This week", dateRange: "from this week to now" },
    { label: "This month", dateRange: "this month" },
    { label: "Last week", dateRange: "Last week" },
    { label: "Last month", dateRange: "Last month" },
    { label: "Last 7d", dateRange: "Last 7 days" },
    { label: "Last 14d", dateRange: "Last 14 days" },
    { label: "Last 30d", dateRange: "Last 30 days" },
    { label: "Last 365d", dateRange: "Last 365 days" },
  ] as const
).map(({ label, dateRange }) => ({
  label,
  key: dateRange,
  dateRange: insightsDateRangeSchema.parse(dateRange),
}));

type DatePresetEntry = (typeof DATE_PRESETS)[number];
type DatePreset = DatePresetEntry["key"];

export const DEFAULT_DATE_RANGE = insightsDateRangeSchema.parse("Last 30 days");

/** The first and last day a Cube date range covers today, resolved the way the server resolves it. */
export function daysOf(dateRange: InsightsDateRange): DayRange {
  const days = daysNow(dateRange);
  // Unreachable: insightsDateRangeSchema only mints ranges that resolve to days.
  if (isErr(days)) {
    throw new Error(`${JSON.stringify(dateRange)} is not a day range`);
  }
  return days.value;
}

/** The calendar disables days after today, so a range is drawn only up to today. */
export const pickerDatesOf = ({
  from,
  to,
}: {
  from: string;
  to: string;
}): [Date, Date] => {
  const today = toDateStr(new Date());
  return [
    new Date(`${from}T00:00:00`),
    new Date(`${to < today ? to : today}T00:00:00`),
  ];
};

const presetEntryOf = (
  dateRange: DateRange,
): DatePresetEntry | "custom-range" =>
  DATE_PRESETS.find((preset) => preset.key === dateRange) ?? "custom-range";

export const presetNamed = (
  dateRange: DateRange,
): InsightsDateRange | "custom-range" => {
  const preset = presetEntryOf(dateRange);
  return preset === "custom-range" ? preset : preset.dateRange;
};

export const toDatesControl = (
  dateRange: InsightsDateRange,
): {
  days: DayRange;
  preset: DatePreset | "custom-range";
  label: string;
} => {
  const days = daysOf(dateRange);
  const preset = presetEntryOf(dateRange);
  return preset === "custom-range"
    ? { days, preset, label: formatDateRangeLabel(days.from, days.to) }
    : { days, preset: preset.key, label: preset.label };
};

// A preset's key is the Cube date range it sends.
export function buildDateRangePresets(): DateRangePreset[] {
  return DATE_PRESETS.map(({ label, key, dateRange }) => ({
    key,
    label,
    getRange: () => pickerDatesOf(daysOf(dateRange)),
  }));
}
